import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { PlayCircle, Eye, ArrowRight, Sparkles, Calendar } from 'lucide-react';
import toast from 'react-hot-toast';

const backendUrl = 'https://justurbane.in/backend_php/public';

const MagazineDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [magazine, setMagazine] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch magazine by ID
  useEffect(() => {
    const fetchMagazine = async () => {
      try {
        const res = await fetch(`${backendUrl}/api/magazines/${id}`);
        if (!res.ok) throw new Error('Failed to fetch magazine');
        const data = await res.json();
        setMagazine(data);
      } catch (err) {
        console.error(err);
        toast.error('Failed to load magazine');
      } finally {
        setLoading(false);
      }
    };

    fetchMagazine();
  }, [id]);

  if (loading) return <p className="p-4">Loading...</p>;
  if (!magazine) return <p className="p-4">Magazine not found</p>;

  const openMagazineReader = () => {
    // navigate('/magazine-reader');
    navigate('/magazine-reader', { state: { pdfUrl: magazine.pdf_url , displayPages: magazine.display_pages } });      
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-gray-900 via-black to-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-8 py-16 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-flex items-center bg-amber-500 text-black px-6 py-2 rounded-full text-sm font-semibold mb-8">
              <Sparkles className="w-4 h-4 mr-2" />
              DIGITAL MAGAZINE
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              Just Urbane Magazine
            </h1>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Premium lifestyle content featuring luxury fashion, technology, and exclusive interviews
            </p>
          </motion.div>
        </div>
      </div>

      {/* Main Content Section */}
      <div className="max-w-7xl mx-auto px-8 py-16">
        {/* Featured Magazine Grid */}
        <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
          {/* Left: Magazine Cover */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            <div className="relative group cursor-pointer" onClick={openMagazineReader}>
              <div className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl p-6 shadow-2xl group-hover:shadow-3xl transition-all duration-500">
                <img
                  src={magazine.cover_image}
                  alt={magazine.title}
                  className="w-full aspect-[2622/3236] object-cover rounded-xl group-hover:scale-[1.02] transition-transform duration-500"
                />
                <div className="absolute inset-6 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl flex items-center justify-center">
                  <button className="bg-white text-black px-8 py-3 rounded-full font-semibold flex items-center space-x-3 shadow-lg">
                    <Eye className="w-5 h-5" />
                    <span>Read Magazine</span>
                  </button>
                </div>
              </div>
              {/* <div className="absolute -top-3 -right-3 bg-amber-500 text-black px-4 py-2 rounded-full text-sm font-bold shadow-lg">
                Latest Issue
              </div> */}
            </div>
          </motion.div>

          {/* Right: Magazine Info */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="space-y-8"
          >
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {magazine.title}
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">{magazine.description}</p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6">
              <div className="text-center p-4 bg-gray-50 rounded-xl">
                <div className="text-2xl font-bold text-gray-900">{magazine.pages}</div>
                <div className="text-sm text-gray-600">Pages</div>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-xl">
                <div className="text-2xl font-bold text-gray-900">{magazine.articles_count}</div>
                <div className="text-sm text-gray-600">Articles</div>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-xl">
                <div className="text-2xl font-bold text-gray-900">{magazine.read_time}</div>
                <div className="text-sm text-gray-600">Min Read</div>
              </div>
            </div>

            {/* Features */}
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-gray-900">What's Inside</h3>
              <div className="space-y-3">
                {[
                  'Exclusive Celebrity Interviews',
                  'Luxury Travel Destinations',
                  'Premium Fashion Collections',
                  'Latest Technology Reviews',
                  'Art & Culture Features',
                ].map((feature, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                    <span className="text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              {magazine.pdf_url && (
                <a
                  onClick={openMagazineReader}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-black text-white px-8 py-4 rounded-xl font-semibold hover:bg-gray-800 transition-colors duration-300 flex items-center justify-center space-x-3"
                >
                  <PlayCircle className="w-5 h-5" />
                  <span>Read Now</span>
                </a>
              )}

              <Link
                to="/pricing"
                className="border-2 border-black text-black px-8 py-4 rounded-xl font-semibold hover:bg-black hover:text-white transition-all duration-300 flex items-center justify-center space-x-3"
              >
                <span>Subscribe</span>
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Additional sections can remain unchanged */}
      </div>
    </div>
  );
};

export default MagazineDetailPage;
